import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface SchoolFormData {
  name: string;
  address: string;
}

export interface SchoolFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (formData: SchoolFormData) => Promise<void>;
  formData: SchoolFormData;
  onFormChange: (field: string, value: string) => void;
  isSaving: boolean;
}

const SchoolFormModal: React.FC<SchoolFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  formData,
  onFormChange,
  isSaving,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async () => {
    await onSubmit(formData);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    onFormChange(name, value);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      disableEscapeKeyDown={isSaving}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 3,
          position: 'relative',
        }
      }}
    >
      <IconButton
        onClick={onClose}
        disabled={isSaving}
        className="close-button"
        sx={{ color: '#666' }}
      >
        <CloseIcon />
      </IconButton>

      <DialogTitle 
        sx={{ 
          textAlign: 'center',
          pb: 2,
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2
        }}
      >
        <div 
          className="modal-icon" 
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
          }}
        >
          <SchoolIcon sx={{ color: 'white', fontSize: 32 }} />
        </div>
        {t('addSchool')}
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, px: 3 }}>
        <div className="form-container">
          <TextField
            fullWidth
            margin="normal"
            label={t("schoolName")}
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            disabled={isSaving}
            required
          />
          
          <TextField
            fullWidth
            margin="normal"
            label={t("address")}
            name="address"
            value={formData.address}
            onChange={handleInputChange}
            disabled={isSaving}
            multiline
            rows={2}
          />
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }} style={{ marginBottom: 0 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={isSaving}
          sx={{ minWidth: 100 }}
        >
          {t("cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isSaving || !formData.name.trim()}
          sx={{ minWidth: 100 }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              {t("adding")}
            </>
          ) : (
            t("add")
          )}
        </Button>
      </DialogActions>

      {/* Loading Overlay */}
      {isSaving && (
        <div 
          className="loading-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
            borderRadius: isMobile ? 0 : 12,
          }}
        >
          <CircularProgress 
            size={60}
            sx={{
              color: '#667eea',
              mb: 2
            }}
          />
          <div 
            className="loading-text"
            style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: '#2c3e50',
              textAlign: 'center',
              marginBottom: '8px'
            }}
          >
            {t("creatingSchool")}
          </div>
          <div 
            style={{
              fontSize: '14px',
              color: '#7f8c8d',
              textAlign: 'center'
            }}
          >
            {t("pleaseWait")}
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default SchoolFormModal;